import { type CSSProperties, useMemo } from "react";
import { type WindowInfo, type WindowInstanceId, type WindowPosition, useDesktop } from "./DesktopContext";
import { DragItemType } from "./DragItemType";
import type { TrackId } from "./TrackId";
import type { WindowElement } from "./Window";
import type { WindowKind } from "./WindowKind";

export function useTracks(
  trackIds: TrackId[],
  renderEmptyTracks: boolean,
  filter?: (_: {
    position: WindowPosition;
    instanceId: WindowInstanceId;
  }) => boolean,
) {
  const desktop = useDesktop();
  const windowMap = useMemo(() => desktop.windowMap(), [desktop]);
  const windowOrder = useMemo(() => desktop.windowOrder(), [desktop]);

  const { tracks, unmanaged } = useMemo(() => {
    const tracks: Map<TrackId, { instanceId: WindowInstanceId; renderer: () => WindowElement }[]> = renderEmptyTracks
      ? new Map(trackIds.map((trackId) => [trackId, []]))
      : new Map([]);
    const unmanaged: {
      instanceId: WindowInstanceId;
      renderer: () => WindowElement;
    }[] = [];

    for (const windowId of windowOrder) {
      const windowInfo = windowMap.get(windowId);
      if (!windowInfo) {
        continue;
      }

      const {
        state: { position, customProps, size },
        factory: { component, ...windowProps },
      } = windowInfo;

      if (filter && !filter({ instanceId: windowId, position })) {
        continue;
      }

      const onClick = () => desktop.dispatch({ type: "wasUsed", windowId });
      const zIndex = windowOrder.indexOf(windowId);

      if (desktop.isWindowVisible(windowId) && trackIds.includes(position.trackId)) {
        if (position.isManaged) {
          const trackWindows = tracks.get(position.trackId) ?? [];

          const style: CSSProperties = { zIndex };
          const renderer = () =>
            component(windowId, {
              ...windowProps,
              instanceId: windowId,
              draggable: windowProps.draggable === undefined ? true : windowProps.draggable,
              customProps,
              position,
              size,
              componentFactory: component,
              style,
              onClick,
            });
          trackWindows.push({ instanceId: windowId, renderer });

          tracks.set(position.trackId, trackWindows);
        } else {
          const style: CSSProperties = {
            position: "absolute" as const,
            left: position.left,
            top: position.top,
            zIndex,
          };
          const renderer = () =>
            component(windowId, {
              ...windowProps,
              instanceId: windowId,
              draggable: windowProps.draggable === undefined ? true : windowProps.draggable,
              customProps,
              position,
              size,
              componentFactory: component,
              style,
              onClick,
            });
          unmanaged.push({ instanceId: windowId, renderer });
        }
      }
    }

    return {
      tracks,
      unmanaged,
    };
  }, [renderEmptyTracks, trackIds, windowMap, windowOrder, filter, desktop]);

  return {
    tracks,
    unmanaged,
  };
}

export function useIsModalVisible() {
  const desktop = useDesktop();
  const windows = useMemo(() => desktop.windows(), [desktop]);

  let isVisible = false;

  for (const window of windows) {
    isVisible = isVisible || (window.state.isVisible && window.factory.dragItemType === DragItemType.ModalWindow);
  }

  return isVisible;
}
